import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '@/modules/tenant/services/tenantApi';

export interface DealershipSettings {
  id: string;
  tenant_id: string;
  /** #870 — manufacturer brands the dealership carries (AI CARRIED_BRANDS). */
  carried_manufacturers?: string[] | null;
  // #1029 — AI Configuration moved to the platform app's per-tenant admin
  // area. ai_enabled is the ONE surviving AI field: read-only here, still
  // required by modules/inventory/hooks/useAiFeatureFlag.ts to gate every
  // dealer-visible AI button.
  ai_enabled?: boolean;
  default_interest_rate?: number | null;
  default_down_payment_pct?: number | null;
  default_financing_term_months?: number | null;
  default_location_id?: string | null;
  logo_path?: string;
  light_logo_path?: string;
  dark_logo_path?: string;
  logo_url?: string;
  light_logo_url?: string;
  dark_logo_url?: string;
  quote_email_settings?: Record<string, unknown>;
  quote_pdf_settings?: Record<string, unknown>;
  default_doc_fee?: number | string;
  default_freight_fee?: number | string;
  default_prep_fee?: number | string;
  woocommerce_enabled?: boolean;
  woocommerce_url?: string | null;
  woocommerce_username?: string | null;
  woocommerce_sync_enabled?: boolean;
  woocommerce_sync_interval_minutes?: number | null;
  woocommerce_last_synced_at?: string | null;
  sync_direction?: 'inbound_only' | 'outbound_only' | 'bidirectional' | 'paused';
  // #733 — manual-dispatch configuration. timeout is in WORKING minutes.
  lead_dispatch_notify_enabled?: boolean;
  lead_dispatch_timeout_minutes?: number;
  // #706 — routing mode (Tier-1 enum; contract carries the same union).
  lead_assignment_mode?: 'manual_dispatch' | 'auto_round_robin';
  /** D8-03 / #408 — the shop's single hourly labour rate, snapshotted onto a job at invoice time. */
  labour_rate_cents?: number;
  /** D8-03 / #408 — invoice payment terms in days; 0 means due on receipt, dealer-local. */
  payment_terms_days?: number;
  /** D8-03 / #408 — the tenant's single currency, mirroring the contract's DealershipSettings shape. */
  currency_code?: string;
  /** DEP-01 / D11-08 / #408 — the deposit policy kind; `none` computes no deposit regardless of require_deposit_before_work. */
  deposit_policy_kind?: 'none' | 'percentage' | 'fixed';
  /** DEP-01 / D11-08 / #408 — deposit percentage of the estimate, used only when deposit_policy_kind is `percentage`. */
  deposit_policy_percentage?: number | null;
  /** DEP-01 / D11-08 / #408 — deposit amount in integer cents, used only when deposit_policy_kind is `fixed`. */
  deposit_policy_fixed_cents?: number | null;
  /** DEP-01 / D11-08 / #408 — whether a deposit must be collected before work starts. */
  require_deposit_before_work?: boolean;
  /** #408 (D-12) — Phase 20 service settings: time tracking policy for jobs. */
  time_tracking_mode?: 'disabled' | 'optional' | 'mandatory';
  /** #408 (D-12) — Phase 20 service settings: auto-generate spring-launch campaigns. */
  auto_generate_spring_launch_campaigns?: boolean;
  /** #408 (D-12) — Phase 20 service settings: days past due before a subscription is flagged (soft). */
  subscription_grace_days?: number;
  /** #408 (D-12) — Phase 20 service settings: days past due before a subscription lapses (hard); must exceed soft. */
  subscription_hard_grace_days?: number;
  /** D18-03 / D18-26 / #408 — the dealer's resolved subcontractor specialty vocabulary; never null. */
  subcontractor_specialties: string[];
  created_at: string;
  updated_at: string;
}

export interface DealershipSettingsUpdateInput {
  /** D18-03 / #408 — replaces the dealer's subcontractor specialty vocabulary; null resets to the defaults. */
  subcontractor_specialties?: string[] | null;
  /** #870 — manufacturer brands the dealership carries. */
  carried_manufacturers?: string[];
  default_interest_rate?: number | null;
  default_down_payment_pct?: number | null;
  default_financing_term_months?: number | null;
  default_location_id?: string | null;
  logo_path?: string;
  light_logo_path?: string;
  dark_logo_path?: string;
  logo_url?: string;
  light_logo_url?: string;
  dark_logo_url?: string;
  quote_email_settings?: Record<string, unknown>;
  quote_pdf_settings?: Record<string, unknown>;
  default_doc_fee?: number;
  default_freight_fee?: number;
  default_prep_fee?: number;
  woocommerce_enabled?: boolean;
  woocommerce_url?: string | null;
  woocommerce_username?: string | null;
  woocommerce_app_password?: string;
  woocommerce_sync_enabled?: boolean;
  woocommerce_sync_interval_minutes?: number | null;
  sync_direction?: 'inbound_only' | 'outbound_only' | 'bidirectional' | 'paused';
  // #733 — manual-dispatch configuration. timeout is in WORKING minutes.
  lead_dispatch_notify_enabled?: boolean;
  lead_dispatch_timeout_minutes?: number;
  // #706 — routing mode (Tier-1 enum; contract carries the same union).
  lead_assignment_mode?: 'manual_dispatch' | 'auto_round_robin';
  /** D8-03 / #408 — the shop's single hourly labour rate, snapshotted onto a job at invoice time. */
  labour_rate_cents?: number;
  /** D8-03 / #408 — invoice payment terms in days; 0 means due on receipt, dealer-local. */
  payment_terms_days?: number;
  /** D8-03 / #408 — the tenant's single currency, mirroring the contract's DealershipSettings shape. */
  currency_code?: string;
  /** DEP-01 / D11-08 / #408 — the deposit policy kind; `none` computes no deposit regardless of require_deposit_before_work. */
  deposit_policy_kind?: 'none' | 'percentage' | 'fixed';
  /** DEP-01 / D11-08 / #408 — deposit percentage of the estimate, used only when deposit_policy_kind is `percentage`. */
  deposit_policy_percentage?: number | null;
  /** DEP-01 / D11-08 / #408 — deposit amount in integer cents, used only when deposit_policy_kind is `fixed`. */
  deposit_policy_fixed_cents?: number | null;
  /** DEP-01 / D11-08 / #408 — whether a deposit must be collected before work starts. */
  require_deposit_before_work?: boolean;
  /** #408 (D-12) — Phase 20 service settings: time tracking policy for jobs. */
  time_tracking_mode?: 'disabled' | 'optional' | 'mandatory';
  /** #408 (D-12) — Phase 20 service settings: auto-generate spring-launch campaigns. */
  auto_generate_spring_launch_campaigns?: boolean;
  /** #408 (D-12) — Phase 20 service settings: days past due before a subscription is flagged (soft). */
  subscription_grace_days?: number;
  /** #408 (D-12) — Phase 20 service settings: days past due before a subscription lapses (hard); must exceed soft. */
  subscription_hard_grace_days?: number;
}

/**
 * Get dealership settings
 */
export function useDealershipSettings() {
  return useQuery({
    queryKey: ['dealership-settings'],
    queryFn: () => tenantApi.getOne<DealershipSettings>('/settings/dealership'),
  });
}

/**
 * Update dealership settings
 */
export function useUpdateDealershipSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DealershipSettingsUpdateInput) =>
      tenantApi.putOne<DealershipSettings>('/settings/dealership', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealership-settings'] });
    },
  });
}

// Phase 39+: useTestAIConnection / useTestPerplexityConnection removed.
// Provider keys are platform-owned; tenant-side connection tests have no
// key to test against. Admins verify keys from Platform → AI Settings.

/**
 * Upload dealership logo (or remove it)
 */
export function useUploadDealershipLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { logo?: File; removeLogo?: boolean; logoType?: 'light' | 'dark' }) => {
      const formData = new FormData();

      if (data.logo) {
        formData.append('logo', data.logo);
      }

      if (data.removeLogo) {
        formData.append('remove_logo', '1');
      }

      if (data.logoType) {
        formData.append('logo_type', data.logoType);
      }

      return tenantApi.postOne<DealershipSettings>('/settings/dealership/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealership-settings'] });
    },
  });
}
